import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useNavigate } from 'react-router';

import api, { isForcedLogoutError } from '../../axios/axiosConfig';
import { useAuth } from '../../context/AuthContext';
import { useBrandContext } from '../../context/BrandContext';
import { useToast } from '../../context/ToastModalContext';

import Button from '../../components/button/Button';
import Input from '../../components/input/Input';
import Modal from '../../components/modal/Modal';

import {
  getUser,
  unRegisterPushNotification,
  updateUser,
} from '../../services/userService';

import addCircle from '../../assets/check_circle_outline.svg';
import { ReactComponent as LoaderSecundary } from '../../assets/SpinnerBlack.svg';
import {
  convertBirthDate,
  validateCellNumber,
  validateCompletName,
  validatePassword,
} from '../../utils/format';
import { validate_CPF } from '../../utils/validate_cpf';
import { onlyDigits, toUf } from '../../utils/billingAddress';
import { lookupCep } from '../../services/cepService';

import './UserProfile.css';
import { useHeaderContext } from '../../context/HeaderContext';

/** 01310100 -> 01310-100. */
const formatCep = (value?: string | null) => {
  const digits = onlyDigits(value).slice(0, 8);
  return digits.length > 5 ? `${digits.slice(0, 5)}-${digits.slice(5)}` : digits;
};

const UserProfile = () => {
  type Inputs = {
    name: string;
    email: string;
    cpf: string;
    birthday: string;
    cell: string;
    cep: string;
    street: string;
    number: string;
    complement: string;
    neighborhood: string;
    city: string;
    uf: string;
  };
  const [form, setForm] = useState<Inputs>({
    name: '',
    email: '',
    cpf: '',
    birthday: '',
    cell: '',
    cep: '',
    street: '',
    number: '',
    complement: '',
    neighborhood: '',
    city: '',
    uf: '',
  });
  type Errors = Partial<Record<keyof Inputs, string>>;
  const [errorsValidate, setErrorsValidate] = useState<Errors>({});
  const [errorFetch, setErrorFetch] = useState('');
  const [isErrorUserInfo, setIsErrorUserInfo] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [userInfo, setUserInfo] = useState<any>([]);
  const [isModalSignOutOpen, setIsModalSignOutOpen] = useState<boolean>(false);
  const navigate = useNavigate();
  const { showToast } = useToast();
  const { state } = useLocation();
  const brandContext = useBrandContext();
  const { setHeaderConfig } = useHeaderContext();
  const authContext = useAuth();
  const cpfRequired = state?.cpfRequired ? state.cpfRequired : false;
  // Endereco de cobranca exigido pela Pagar.me: quem chega por aqui veio de uma
  // tela de cartao (cadastrar ou adicionar credito) e volta para ela ao salvar.
  const addressRequired = state?.addressRequired ? state.addressRequired : false;
  const returnTo: string | undefined = state?.returnTo;
  const addressSectionRef = useRef<HTMLDivElement>(null);
  const [isLookingUpCep, setIsLookingUpCep] = useState(false);

  const handleCheckUserActiveTransactions = (data: Array<any>) => {
    if (data.length > 0) {
      showToast({
        messageModal:
          'Recarga em andamento! Aguarde o término da recarga para realizar a exclusão da sua conta.',
        allowButtonClosed: true,
        closeButtonText: 'Entendi',
      });
    } else {
      navigate(`/${brandContext.brandName}/accountDeletion`);
    }
  };

  const checkUserActiveTransactions = async () => {
    try {
      const response = await api.get(`/UserActiveTransactions`);

      handleCheckUserActiveTransactions(response.data);
    } catch (error) {
      throw error;
    }
  };

  useEffect(() => {
    if (authContext.isLogged() === undefined) return;
    else if (authContext.isLogged()) getUserProfileData();
    else navigate(`/${brandContext.brandName}/`);
  }, [authContext.isLogged]);

  const getUserProfileData = async () => {
    try {
      const response = await getUser();
      if (response?.email) {
        setForm({
          ...form,
          cell: response?.phoneNumber ? response?.phoneNumber : '',
          email: response?.email ? response?.email : '',
          name: response?.name ? response?.name : '',
          birthday: response?.birthdate
            ? `${response?.birthdate?.split('-')[2]}/${
                response?.birthdate?.split('-')[1]
              }/${response?.birthdate?.split('-')[0]}`
            : '',
          cpf: response?.cpf ? response?.cpf : '',
          cep: formatCep(response?.cep),
          street: response?.address || '',
          number:
            response?.number !== null && response?.number !== undefined
              ? String(response.number)
              : '',
          complement: response?.complement || '',
          neighborhood: response?.anuNeighborhood || '',
          city: response?.city || '',
          uf: toUf(response?.state) || '',
        });
      }
      setUserInfo(response);
      setIsLoading(false);
      setIsErrorUserInfo(false);
      setErrorFetch('');
      return response;
    } catch (error: any) {
      setIsLoading(false);

      // Sessao derrubada (401/403/404): o usuario ja esta indo para o login,
      // exibir o erro so confundiria.
      if (isForcedLogoutError(error)) return;

      setIsErrorUserInfo(true);
      showToast({
        title: 'Falha',
        messageModal:
          'Falha ao carregar informações do usuário. Por favor, tente novamente!!!!',
        textEnterButton: 'Tentar novamente',
        allowButtonClosed: true,
        handleEnterButton: () => getUserProfileData(),
        closeButtonText: 'Fechar',
      });
    }
  };

  const validate = (newInputs: Inputs): Errors => {
    let newErrors: Errors = {};
    if (!newInputs.name.trim()) {
      newErrors.name = 'Campo Obrigatório';
    } else if (!validateCompletName(newInputs.name)) {
      newErrors.name = 'Por favor insira nome e sobrenome';
    }

    if (newInputs?.cell?.trim() && !validateCellNumber(newInputs.cell)) {
      newErrors.cell = 'Por favor insira um número válido.';
    }

    if (newInputs?.birthday?.trim() && !validatePassword(newInputs.birthday)) {
      newErrors.birthday = 'Por favor insira uma data de nascimento válida.';
    }

    if (
      (newInputs?.cpf?.trim() || cpfRequired) &&
      !validate_CPF(newInputs.cpf)
    ) {
      newErrors.cpf = 'Por favor insira um cpf válido.';
    }

    // Endereco: obrigatorio so quando a tela de cartao mandou o usuario para ca.
    // Fora disso, o que for digitado precisa estar coerente, mas pode ficar vazio.
    const cepDigits = onlyDigits(newInputs.cep);
    if ((cepDigits || addressRequired) && cepDigits.length !== 8) {
      newErrors.cep = 'Por favor insira um CEP válido.';
    }
    if (addressRequired && !newInputs.street.trim()) {
      newErrors.street = 'Campo Obrigatório';
    }
    if (addressRequired && !newInputs.city.trim()) {
      newErrors.city = 'Campo Obrigatório';
    }
    if ((newInputs.uf.trim() || addressRequired) && !toUf(newInputs.uf)) {
      newErrors.uf = 'UF inválida';
    }
    setErrorsValidate(newErrors);
    return newErrors;
  };
  const handleKeyPress = (event: any) => {
    if (event.key === ' ') {
      event.preventDefault();
    }
  };

  const handleSubmit = async (event: { preventDefault: () => void }) => {
    event.preventDefault();
    const errors = validate(form);
    if (Object.keys(errors).length === 0) {
      setIsLoading(true);
      const newSaveUser: any = {
        id: userInfo.id,
        email: form.email,
        phoneNumber: form.cell ? form.cell : null,
        name: form.name,
        cpf: form.cpf,
        birthdate: form.birthday ? convertBirthDate(form.birthday) : null,
        // O PUT so altera o campo que vier preenchido: vazio vai nulo e nao
        // apaga o que esta gravado.
        cep: onlyDigits(form.cep) || null,
        address: form.street.trim() || null,
        number: onlyDigits(form.number) ? Number(onlyDigits(form.number)) : null,
        complement: form.complement.trim() || null,
        anuNeighborhood: form.neighborhood.trim() || null,
        city: form.city.trim() || null,
        state: toUf(form.uf),
      };
      try {
        const userUpdateApi = await updateUser(newSaveUser);
        if (userUpdateApi.status === 204) {
          const response = await getUserProfileData();
          authContext.setUser(response);
          setErrorFetch('');
          if (addressRequired && returnTo) {
            // Volta para a tela de cartao de onde o usuario veio.
            navigate(returnTo, { replace: true });
            showToast({
              title: 'Endereço salvo',
              messageModal: 'Agora você já pode continuar com o seu cartão.',
              allowButtonClosed: true,
              closeButtonText: 'OK',
            });
            return;
          }
          showToast({
            title: 'Alteração salva com sucesso',
            allowButtonClosed: true,
            closeButtonText: 'OK',
          });
        } else {
          setErrorFetch(
            'Falha ao salvar alteração. Por favor, tente novamente.'
          );
        }
      } catch (error: any) {
        if (error?.response?.status === 409) {
          setErrorFetch('Este e-mail já possui uma conta.');
        } else if (error?.message === 'Network Error') {
          setErrorFetch(
            'Sua conexão está instável. Por favor, tente mais tarde.'
          );
        } else {
          setErrorFetch('Falha no servidor. Por favor tente novamente.');
        }
      } finally {
        setIsLoading(false);
      }
    }
  };

  const handleSignOut = () => {
    const pushToken = authContext.storedDevicePushToken || authContext.expoPushToken;
    // Usar o tipo específico do device token ou fallback para expo
    let notificationProvider = 'expo';
    if (authContext.storedDevicePushToken) {
      notificationProvider = authContext.storedDevicePushTokenType || 'expo';
    }

    unRegisterPushNotification(
      pushToken,
      authContext.platform,
      authContext.appVersion,
      notificationProvider,
      brandContext.appId
    );
    authContext.setToken(null);
    navigate(`/${brandContext.brandName}/`);
  };

  const handleCellNumberChange = (event: any) => {
    let phoneNumber = event.target.value.replace(/\D/g, '');

    phoneNumber = phoneNumber.slice(0, 11);
    if (phoneNumber.length > 10) {
      phoneNumber = phoneNumber.replace(
        /^(\d{2})(\d{5})(\d{4})$/,
        '($1) $2-$3'
      );
    } else {
      phoneNumber = phoneNumber.replace(/^(\d{1})(\d{4})(\d{4})$/, '$1 $2-$3');
    }

    setForm({ ...form, cell: phoneNumber });
  };

  const handleBirthdayDateChange = (event: any) => {
    let birthdate = event.target.value.replace(/\D/g, '');
    birthdate = birthdate.slice(0, 8);
    birthdate = birthdate.replace(/^(\d{2})(\d{2})(\d{4})$/, '$1/$2/$3');

    setForm({ ...form, birthday: birthdate });
  };

  const handleCPFChange = (event: any) => {
    let inputCPF = event.target.value.replace(/\D/g, '');
    inputCPF = inputCPF.slice(0, 11);
    if (inputCPF.length > 9) {
      inputCPF = inputCPF.replace(
        /(\d{3})(\d{3})(\d{3})(\d{2})/,
        '$1.$2.$3-$4'
      );
    } else if (inputCPF.length > 6) {
      inputCPF = inputCPF.replace(/(\d{3})(\d{3})(\d{3})/, '$1.$2.$3');
    } else if (inputCPF.length > 3) {
      inputCPF = inputCPF.replace(/(\d{3})(\d{3})/, '$1.$2');
    }

    setForm({ ...form, cpf: inputCPF });
  };

  const handleCepChange = async (event: any) => {
    const digits = onlyDigits(event.target.value).slice(0, 8);
    setForm((current) => ({ ...current, cep: formatCep(digits) }));

    if (digits.length !== 8) return;

    // Preenche rua, bairro, cidade e UF pelo CEP; numero e complemento ficam
    // com o usuario. Sem resposta, ele digita na mao.
    setIsLookingUpCep(true);
    const found = await lookupCep(digits);
    setIsLookingUpCep(false);

    if (found) {
      setForm((current) => ({
        ...current,
        street: found.street || current.street,
        neighborhood: found.neighborhood || current.neighborhood,
        city: found.city || current.city,
        uf: found.state || current.uf,
      }));
    }
  };

  const handleTermsClick = () => {
    if (brandContext.brandData?.appTermsAndConditionsLink) {
      window.open(
        `/redir?directRedir=${brandContext.brandData.appTermsAndConditionsLink}`,
        '_blank'
      );
    } else {
      navigate(`/${brandContext.brandName}/terms-of-use`);
    }
  };

  const handlePolicyClick = () => {
    if (brandContext.brandData?.appPrivacyPolicyLink) {
      window.open(
        `/redir?directRedir=${brandContext.brandData.appPrivacyPolicyLink}`,
        '_blank'
      );
    } else {
      navigate(`/${brandContext.brandName}/privacy-policy`);
    }
  };

  // Quem veio exigido pela tela de cartao cai direto na secao de endereco.
  useEffect(() => {
    if (addressRequired && userInfo?.name) {
      addressSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [addressRequired, userInfo?.name]);

  useEffect(() => {
    setHeaderConfig({
      headerText: authContext?.user?.name,
      backNavigation: () => navigate(`/${brandContext.brandName}/`),
    });
    return () => setHeaderConfig({});
  }, [authContext.user, brandContext.brandName, navigate, setHeaderConfig]);

  return (
    <div
      style={
        isModalSignOutOpen
          ? {
              width: '100vw',
              height: '100vh',
              position: 'fixed',
              top: 0,
              left: 0,
            }
          : {}
      }
    >
      {userInfo?.name && !isErrorUserInfo ? (
        <div className='user-profile-container'>
          <form onSubmit={handleSubmit} className='user-profile-form'>
            <div className='user-profile-fields'>
              <Input
                labelName='Nome completo'
                htmlFor='name'
                type='text'
                id='name'
                className={
                  errorsValidate.name
                    ? 'input-pattern error-input'
                    : 'input-pattern'
                }
                placeholder='Insira seu nome completo'
                value={form.name}
                onChange={(event) => {
                  setForm({ ...form, name: event.target.value });
                }}
                isErrorValidate={errorsValidate.name ? true : false}
                messageError={errorsValidate.name}
              />
              <div className='user-profile-flex-inputs'>
                <Input
                  labelName='Data de nascimento'
                  htmlFor='birthday'
                  type='text'
                  id='birthday'
                  style={{ width: 'calc(100% - 22px)' }}
                  className={
                    errorsValidate.birthday
                      ? 'input-pattern error-input'
                      : 'input-pattern'
                  }
                  placeholder='Insira sua data de nascimento'
                  value={form.birthday}
                  onKeyDown={(event) => handleKeyPress(event)}
                  onChange={(event) => handleBirthdayDateChange(event)}
                  isErrorValidate={errorsValidate.birthday ? true : false}
                  messageError={errorsValidate.birthday}
                />
                <Input
                  labelName='Celular'
                  htmlFor='cell'
                  type='text'
                  id='cell'
                  style={{ width: 'calc(100% - 22px)' }}
                  className={
                    errorsValidate.cell
                      ? 'input-pattern error-input'
                      : 'input-pattern'
                  }
                  placeholder='Insira seu número de celular'
                  value={form.cell}
                  onKeyDown={(event) => handleKeyPress(event)}
                  onChange={(event) => handleCellNumberChange(event)}
                  isErrorValidate={errorsValidate.cell ? true : false}
                  messageError={errorsValidate.cell}
                />
              </div>
              <Input
                labelName='CPF'
                htmlFor='cpf'
                type='text'
                id='cpf'
                className={
                  errorsValidate.cpf
                    ? 'input-pattern error-input'
                    : 'input-pattern'
                }
                placeholder='Insira seu cpf'
                value={form.cpf}
                onKeyDown={(event) => handleKeyPress(event)}
                onChange={(event) => handleCPFChange(event)}
                isErrorValidate={errorsValidate.cpf ? true : false}
                messageError={errorsValidate.cpf}
              />
              <Input
                labelName='E-mail'
                htmlFor='email'
                type='text'
                id='email'
                disabled={true}
                style={{ backgroundColor: 'rgb(170, 178, 175, 0.3)' }}
                className={
                  errorsValidate.email
                    ? 'input-pattern error-input'
                    : 'input-pattern'
                }
                placeholder='Insira seu e-mail'
                value={form.email}
                onKeyDown={(event) => handleKeyPress(event)}
                onChange={(event) => {
                  setForm({ ...form, email: event.target.value });
                }}
                isErrorValidate={errorsValidate.email ? true : false}
                messageError={errorsValidate.email}
              />
              <div ref={addressSectionRef} className='user-profile-address'>
                <p className='user-profile-address-title'>Endereço</p>
                {addressRequired && (
                  <p className='user-profile-address-hint'>
                    Para usar este cartão, precisamos do seu endereço de
                    cobrança.
                  </p>
                )}
              </div>
              <div className='user-profile-flex-inputs'>
                <Input
                  labelName={isLookingUpCep ? 'CEP (buscando...)' : 'CEP'}
                  htmlFor='cep'
                  type='text'
                  id='cep'
                  style={{ width: 'calc(100% - 22px)' }}
                  className={
                    errorsValidate.cep
                      ? 'input-pattern error-input'
                      : 'input-pattern'
                  }
                  placeholder='00000-000'
                  value={form.cep}
                  maxLength={9}
                  onChange={(event) => handleCepChange(event)}
                  isErrorValidate={errorsValidate.cep ? true : false}
                  messageError={errorsValidate.cep}
                />
                <Input
                  labelName='UF'
                  htmlFor='uf'
                  type='text'
                  id='uf'
                  style={{ width: 'calc(100% - 22px)' }}
                  className={
                    errorsValidate.uf
                      ? 'input-pattern error-input'
                      : 'input-pattern'
                  }
                  placeholder='SP'
                  value={form.uf}
                  maxLength={2}
                  onChange={(event) =>
                    setForm({ ...form, uf: event.target.value.toUpperCase() })
                  }
                  isErrorValidate={errorsValidate.uf ? true : false}
                  messageError={errorsValidate.uf}
                />
              </div>
              <Input
                labelName='Rua'
                htmlFor='street'
                type='text'
                id='street'
                className={
                  errorsValidate.street
                    ? 'input-pattern error-input'
                    : 'input-pattern'
                }
                placeholder='Rua, avenida...'
                value={form.street}
                onChange={(event) =>
                  setForm({ ...form, street: event.target.value })
                }
                isErrorValidate={errorsValidate.street ? true : false}
                messageError={errorsValidate.street}
              />
              <div className='user-profile-flex-inputs'>
                <Input
                  labelName='Número'
                  htmlFor='number'
                  type='text'
                  id='number'
                  style={{ width: 'calc(100% - 22px)' }}
                  className='input-pattern'
                  placeholder='Sem número? Deixe vazio'
                  value={form.number}
                  maxLength={6}
                  onChange={(event) =>
                    setForm({ ...form, number: onlyDigits(event.target.value) })
                  }
                />
                <Input
                  labelName='Complemento'
                  htmlFor='complement'
                  type='text'
                  id='complement'
                  style={{ width: 'calc(100% - 22px)' }}
                  className='input-pattern'
                  placeholder='Apto, bloco...'
                  value={form.complement}
                  onChange={(event) =>
                    setForm({ ...form, complement: event.target.value })
                  }
                />
              </div>
              <Input
                labelName='Bairro'
                htmlFor='neighborhood'
                type='text'
                id='neighborhood'
                className='input-pattern'
                placeholder='Insira seu bairro'
                value={form.neighborhood}
                onChange={(event) =>
                  setForm({ ...form, neighborhood: event.target.value })
                }
              />
              <Input
                labelName='Cidade'
                htmlFor='city'
                type='text'
                id='city'
                className={
                  errorsValidate.city
                    ? 'input-pattern error-input'
                    : 'input-pattern'
                }
                placeholder='Insira sua cidade'
                value={form.city}
                onChange={(event) =>
                  setForm({ ...form, city: event.target.value })
                }
                isErrorValidate={errorsValidate.city ? true : false}
                messageError={errorsValidate.city}
              />
              <b className='error-fetch-user-profile'>
                {' '}
                {errorFetch ? errorFetch : null}
              </b>
              <Button
                type='submit'
                children='Salvar'
                loading={isLoading}
                image={
                  <img
                    width='20px'
                    height='20px'
                    alt='add-button-icon'
                    src={addCircle}
                    style={{ filter: 'brightness(3)' }}
                  />
                }
                spinnerSecondary={true}
                className='btn-outline'
                style={{
                  backgroundColor: brandContext.brandBgColor,
                  color: '#FFFFFF', //
                }}
              />
              <Button
                className='btn-outline'
                type='button'
                children={
                  userInfo?.hasPassword ? 'Alterar senha' : 'Definir senha'
                }
                image={
                  <img
                    width='20px'
                    height='20px'
                    alt='add-button-icon'
                    src={addCircle}
                  />
                }
                onClick={() =>
                  navigate(
                    `/${brandContext.brandName}/switchPassword/${userInfo?.hasPassword}`
                  )
                }
              />
              {/* <Button
                className='btn-outline'
                type='button'
                children='Cartões RFID'
                onClick={() =>
                  navigate(`/${brandContext.brandName}/savedRechargeCards`)
                }
                image={<img alt='add-button-icon' src={chargeCard} />}
              /> */}

              <Button
                className='btn-outline'
                type='button'
                children='Adicionar veículos'
                onClick={() =>
                  navigate(`/${brandContext.brandName}/vehicles-list`)
                }
              />
              <Button
                className='btn-outline'
                type='button'
                children='Cupons'
                onClick={() => navigate(`/${brandContext.brandName}/coupons`)}
              />
              <Button
                className='btn-outline'
                type='button'
                children='Termos de uso'
                onClick={handleTermsClick}
              />
              <Button
                className='btn-outline'
                type='button'
                children='Política de privacidade'
                onClick={handlePolicyClick}
              />
              <Button
                className='btn-outline'
                type='button'
                children='Excluir conta'
                onClick={() => checkUserActiveTransactions()}
              />
              <Button
                className='btn-outline'
                type='button'
                children='Sair do App'
                onClick={() => {
                  setIsModalSignOutOpen(true);
                }}
              />
            </div>
          </form>
        </div>
      ) : (
        !isErrorUserInfo && <LoaderSecundary />
      )}
      <Modal
        isOpen={isModalSignOutOpen}
        onClose={() => setIsModalSignOutOpen(false)}
        textEnterButton='Confirmar'
        handleEnterButton={handleSignOut}
        styleEnterButton={{
          backgroundColor: brandContext.brandBgColor,
          color: '#FFFFFF',
        }}
        allowButtonClosed={true}
        closeButtonText='Cancelar'
        title='Deseja realmente sair do App?'
      >
        {/* <div style={{ marginBottom: '20px' }}>
          Deseja realmente sair do App?
        </div> */}
      </Modal>
    </div>
  );
};

export default UserProfile;
